(() => {
  const componentName = 'pr-navigation';

  if (customElements.get(componentName)) return;

  class PRNavigation extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.drawer = this.querySelector('[data-pr-drawer]');
      this.scrim = this.querySelector('.pr-navigation__scrim');
      this.drawerToggle = this.querySelector('[data-pr-drawer-toggle]');
      this.drawerCloseButtons = this.querySelectorAll('[data-pr-drawer-close]');
      this.localizationPanel = this.querySelector('[data-pr-localization-panel]');
      this.localizationTriggers = this.querySelectorAll('[data-pr-localization-toggle]');
      this.submenuToggles = this.querySelectorAll('[data-pr-submenu-toggle]');
      this.featuredPreviewTriggers = this.querySelectorAll('[data-pr-featured-preview-trigger]');
      this.previousFocus = null;
      this.documentOverflow = '';
      this.drawerCloseTimer = null;
      this.lastPointerType = '';

      this.bindEvents();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.closeDrawer({ restoreFocus: false, immediate: true });
      window.clearTimeout(this.drawerCloseTimer);
    }

    bindEvents() {
      this.drawerToggle?.addEventListener('click', () => {
        if (this.hasAttribute('data-drawer-open')) {
          this.closeDrawer();
        } else {
          this.openDrawer();
        }
      }, { signal: this.signal });

      this.drawerCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });
      });

      this.submenuToggles.forEach((button) => {
        button.addEventListener('click', () => this.toggleSubmenu(button), { signal: this.signal });
      });

      this.localizationTriggers.forEach((button) => {
        button.addEventListener('click', () => this.toggleLocalization(button), { signal: this.signal });
      });

      this.featuredPreviewTriggers.forEach((trigger) => {
        const item = trigger.closest('[data-pr-featured-item]');
        if (!item) return;

        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') this.openFeaturedPreview(trigger);
        }, { signal: this.signal });

        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'touch') this.closeFeaturedPreview(trigger);
        }, { signal: this.signal });

        item.addEventListener('focusin', () => {
          if (this.lastPointerType !== 'touch') this.openFeaturedPreview(trigger);
        }, { signal: this.signal });

        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.closeFeaturedPreview(trigger);
        }, { signal: this.signal });

        trigger.addEventListener('pointerdown', (event) => {
          this.lastPointerType = event.pointerType;
        }, { signal: this.signal });

        trigger.addEventListener('click', (event) => {
          const isTouch = this.lastPointerType === 'touch';
          this.lastPointerType = '';
          if (!isTouch || trigger.getAttribute('aria-expanded') === 'true') return;

          event.preventDefault();
          this.openFeaturedPreview(trigger);
        }, { signal: this.signal });
      });

      this.drawer?.addEventListener('click', (event) => {
        if (event.defaultPrevented) return;
        if (event.target.closest('a')) this.closeDrawer({ restoreFocus: false });
      }, { signal: this.signal });

      document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.signal });
      document.addEventListener('pointerdown', (event) => this.handleOutsidePointer(event), { signal: this.signal });
      window.addEventListener('pr-navigation:opened', (event) => {
        if (event.detail?.source !== this) this.closeDrawer({ restoreFocus: false });
      }, { signal: this.signal });
    }

    openDrawer({ focusDrawer = true } = {}) {
      if (!this.drawer || !this.scrim) return;

      window.dispatchEvent(new CustomEvent('pr-navigation:opened', { detail: { source: this } }));
      window.clearTimeout(this.drawerCloseTimer);

      this.previousFocus = document.activeElement;
      this.documentOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.drawer.hidden = false;
      this.scrim.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerToggle?.setAttribute('aria-expanded', 'true');
      this.setToggleLabel(this.drawerToggle, true);

      requestAnimationFrame(() => {
        this.setAttribute('data-drawer-open', '');
        if (focusDrawer) this.querySelector('.pr-navigation__drawer-close')?.focus({ preventScroll: true });
      });
    }

    closeDrawer({ restoreFocus = true, immediate = false } = {}) {
      if (!this.drawer || !this.scrim) return;

      const wasOpen = this.hasAttribute('data-drawer-open');
      this.removeAttribute('data-drawer-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerToggle?.setAttribute('aria-expanded', 'false');
      this.setToggleLabel(this.drawerToggle, false);
      this.closeLocalization();
      this.closeFeaturedPreviews();
      document.documentElement.style.overflow = this.documentOverflow;

      const finish = () => {
        this.drawer.hidden = true;
        this.scrim.hidden = true;
      };

      window.clearTimeout(this.drawerCloseTimer);
      if (immediate) finish();
      else this.drawerCloseTimer = window.setTimeout(finish, 320);

      if (wasOpen && restoreFocus && this.previousFocus instanceof HTMLElement) {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    toggleSubmenu(button) {
      const submenuId = button.getAttribute('aria-controls');
      const submenu = submenuId ? this.querySelector(`#${CSS.escape(submenuId)}`) : null;
      if (!submenu) return;

      const willOpen = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(willOpen));
      submenu.hidden = !willOpen;
    }

    toggleLocalization(trigger) {
      if (!this.localizationPanel) return;

      const willOpen = this.localizationPanel.hidden;
      if (willOpen && trigger.classList.contains('pr-navigation__market-trigger--desktop')) {
        this.openDrawer({ focusDrawer: false });
      }

      this.localizationPanel.hidden = !willOpen;
      this.localizationTriggers.forEach((button) => button.setAttribute('aria-expanded', String(willOpen)));

      if (willOpen) {
        requestAnimationFrame(() => this.localizationPanel.querySelector('select, button')?.focus({ preventScroll: true }));
      }
    }

    closeLocalization() {
      if (!this.localizationPanel || this.localizationPanel.hidden) return;
      this.localizationPanel.hidden = true;
      this.localizationTriggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    }

    openFeaturedPreview(trigger) {
      const previewId = trigger.getAttribute('aria-controls');
      const preview = previewId ? this.querySelector(`#${CSS.escape(previewId)}`) : null;
      const item = trigger.closest('[data-pr-featured-item]');
      if (!preview || !item) return;

      this.closeFeaturedPreviews(trigger);
      item.setAttribute('data-preview-open', '');
      trigger.setAttribute('aria-expanded', 'true');
      preview.setAttribute('aria-hidden', 'false');
    }

    closeFeaturedPreview(trigger) {
      const previewId = trigger.getAttribute('aria-controls');
      const preview = previewId ? this.querySelector(`#${CSS.escape(previewId)}`) : null;
      const item = trigger.closest('[data-pr-featured-item]');
      if (!preview || !item) return;

      item.removeAttribute('data-preview-open');
      trigger.setAttribute('aria-expanded', 'false');
      preview.setAttribute('aria-hidden', 'true');
    }

    closeFeaturedPreviews(exceptTrigger = null) {
      this.featuredPreviewTriggers.forEach((trigger) => {
        if (trigger !== exceptTrigger) this.closeFeaturedPreview(trigger);
      });
    }

    handleOutsidePointer(event) {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('[data-pr-featured-item]')) this.closeFeaturedPreviews();
      if (!this.localizationPanel || this.localizationPanel.hidden) return;
      if (event.target.closest('[data-pr-localization-panel], [data-pr-localization-toggle]')) return;
      this.closeLocalization();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        const expandedPreview = [...this.featuredPreviewTriggers].find(
          (trigger) => trigger.getAttribute('aria-expanded') === 'true'
        );
        if (expandedPreview) {
          this.closeFeaturedPreview(expandedPreview);
          expandedPreview.focus({ preventScroll: true });
          return;
        }
        if (this.localizationPanel && !this.localizationPanel.hidden) {
          this.closeLocalization();
          return;
        }
        if (this.hasAttribute('data-drawer-open')) this.closeDrawer();
        return;
      }

      if (event.key === 'Tab' && this.hasAttribute('data-drawer-open')) this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = [...this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setToggleLabel(button, expanded) {
      if (!button) return;
      const label = expanded ? button.dataset.closeLabel : button.dataset.openLabel;
      if (label) button.setAttribute('aria-label', label);
    }
  }

  customElements.define(componentName, PRNavigation);
})();
